import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Settings } from "lucide-react";

const STORAGE_KEY = "royal_cookie_consent";

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      const t = setTimeout(() => setShow(true), 3500);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = (type) => {
    localStorage.setItem(STORAGE_KEY, type);
    setShow(false);
    setShowModal(false);
  };

  const saveSettings = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ necessary: true, ...prefs }));
    setShow(false);
    setShowModal(false);
  };

  return (
    <>
      <AnimatePresence>
        {show && !showModal && (
          <motion.div
            data-testid="cookie-banner"
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 bg-[#141414] border border-[#5A4A1A]/50 p-6 shadow-[0_0_30px_rgba(0,0,0,0.6)]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-cormorant text-lg italic text-[#F0F0F0]">
                Cookies & Datenschutz
              </h3>
              <button
                onClick={() => accept("necessary")}
                className="text-[#888888] hover:text-[#C9A84C] transition-colors ml-4"
                aria-label="Schließen"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-[#888888] text-xs font-outfit leading-relaxed mb-5">
              Wir nutzen Cookies für ein optimales Erlebnis. Mit deiner Zustimmung
              verbessern wir unsere Dienste.
            </p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="cookie-accept-all"
                onClick={() => accept("all")}
                className="gold-btn-solid text-xs py-2.5 w-full"
              >
                Alle akzeptieren
              </button>
              <button
                data-testid="cookie-necessary-only"
                onClick={() => accept("necessary")}
                className="ghost-btn text-xs py-2.5 w-full"
              >
                Nur notwendige
              </button>
              <button
                data-testid="cookie-settings"
                onClick={() => setShowModal(true)}
                className="flex items-center justify-center gap-2 text-[#888888] text-xs font-outfit hover:text-[#C9A84C] transition-colors py-1.5"
              >
                <Settings size={12} />
                Einstellungen
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            data-testid="cookie-modal"
            className="fixed inset-0 z-[70] bg-[#0A0A0A]/90 flex items-end sm:items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-[#141414] border border-[#5A4A1A]/50 p-8 w-full max-w-md"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-cormorant text-2xl italic text-[#F0F0F0]">
                  Cookie-Einstellungen
                </h3>
                <button onClick={() => setShowModal(false)} className="text-[#888888] hover:text-[#C9A84C] transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-5 mb-8">
                {/* Necessary – always on */}
                <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#5A4A1A]/20">
                  <div>
                    <p className="text-[#F0F0F0] text-sm font-outfit font-medium">Notwendig</p>
                    <p className="text-[#888888] text-xs font-outfit mt-0.5">Immer aktiv</p>
                  </div>
                  <div className="w-8 h-5 bg-[#C9A84C]/30 border border-[#C9A84C]/50 flex items-center justify-end px-0.5">
                    <div className="w-3.5 h-3.5 bg-[#C9A84C]" />
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#5A4A1A]/20">
                  <div>
                    <p className="text-[#F0F0F0] text-sm font-outfit font-medium">Analyse</p>
                    <p className="text-[#888888] text-xs font-outfit mt-0.5">Nutzungsstatistiken</p>
                  </div>
                  <button
                    onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                    data-testid="cookie-toggle-analytics"
                    className={`w-8 h-5 border flex items-center transition-all duration-300 px-0.5 ${prefs.analytics ? "bg-[#C9A84C]/20 border-[#C9A84C]/60 justify-end" : "border-[#5A4A1A] justify-start"}`}
                  >
                    <div className={`w-3.5 h-3.5 transition-colors ${prefs.analytics ? "bg-[#C9A84C]" : "bg-[#888888]"}`} />
                  </button>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#5A4A1A]/20">
                  <div>
                    <p className="text-[#F0F0F0] text-sm font-outfit font-medium">Marketing</p>
                    <p className="text-[#888888] text-xs font-outfit mt-0.5">Personalisierte Inhalte</p>
                  </div>
                  <button
                    onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}
                    data-testid="cookie-toggle-marketing"
                    className={`w-8 h-5 border flex items-center transition-all duration-300 px-0.5 ${prefs.marketing ? "bg-[#C9A84C]/20 border-[#C9A84C]/60 justify-end" : "border-[#5A4A1A] justify-start"}`}
                  >
                    <div className={`w-3.5 h-3.5 transition-colors ${prefs.marketing ? "bg-[#C9A84C]" : "bg-[#888888]"}`} />
                  </button>
                </div>
              </div>

              <button
                data-testid="cookie-save-settings"
                onClick={saveSettings}
                className="gold-btn-solid text-xs py-3 w-full"
              >
                Einstellungen speichern
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
